import axios, { AxiosError } from 'axios';
import { ThunkAction } from 'redux-thunk';
import {
  BillingErrorType,
  IPendingConnection,
  PendingConnectionRequestType,
  TransactionErrorType
} from '../interfaces';
import { OdataComparator } from '@pec/aion-ui-odata/types/odataComparator';
import { ApproveIncomingConnectionsParams, ApproveIncomingConnectionStatus } from 'interfaces/connections';
import { RootState } from '../combineReducers';
import { sendError } from '@pec/aion-ui-core/sendError';
import { QueryBuilder } from '@pec/aion-ui-odata/builders/odataQueryBuilder';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';
import { IConnection } from '@pec/aion-ui-core/types/activeConnections.';
import { ActiveConnectionsResponseV302 } from '@pec/aion-ui-core/slices/activeConnections/activeConnectionsSlice';
import {
  APPLY_INCOMING_CONNECTIONS_REQUEST,
  APPLY_INCOMING_CONNECTIONS_SUCCESS,
  FETCH_ACTIVE_CONNECTIONS_FAILURE,
  FETCH_ACTIVE_CONNECTIONS_REQUEST,
  FETCH_ACTIVE_CONNECTIONS_SUCCESS,
  FETCH_ALL_PENDING_CONNECTIONS_FAILURE,
  FETCH_ALL_PENDING_CONNECTIONS_REQUEST,
  FETCH_ALL_PENDING_CONNECTIONS_SUCCESS,
  FETCH_CONNECTIONS_WITH_NO_SERVICES_COUNT_FAILURE,
  FETCH_CONNECTIONS_WITH_NO_SERVICES_COUNT_REQUEST,
  FETCH_CONNECTIONS_WITH_NO_SERVICES_COUNT_SUCCESS,
  FETCH_PENDING_CONNECTIONS_COUNT_FAILURE,
  FETCH_PENDING_CONNECTIONS_COUNT_REQUEST,
  FETCH_PENDING_CONNECTIONS_COUNT_SUCCESS
} from '../constants/connections';
import { Actions as BillingErrorsActions, errorCatch, insufficientFundsErrorCatch } from './billingErrors';

const fetchAllPendingConnectionsRequest = () =>
  ({
    type: FETCH_ALL_PENDING_CONNECTIONS_REQUEST
  } as const);

const fetchAllPendingConnectionsSuccess = (payload: IPendingConnection[]) =>
  ({
    type: FETCH_ALL_PENDING_CONNECTIONS_SUCCESS,
    payload
  } as const);

const fetchAllPendingConnectionsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_ALL_PENDING_CONNECTIONS_FAILURE,
    error
  } as const;
};

export const fetchAllPendingConnections = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchAllPendingConnectionsRequest());

    const sortOrder = `dateRequested asc`;
    const count = 100;
    const value = [];
    let countRecords = 0;
    let page = 1;

    do {
      const params = new QueryBuilder()
        .orderBy(sortOrder)
        .top(count)
        .skipByPage(page.toString(), count)
        .toQueryParam();

      const { data } = await axios.get<OdataResponse<IPendingConnection[]>>(
        `/api/v3.1/organizations/${organizationId}/pendingConnections`,
        { params }
      );
      countRecords = data['@odata.count'];
      value.push(...data.value);
      page++;
    } while (countRecords > (page - 1) * count);

    dispatch(fetchAllPendingConnectionsSuccess(value));
    return value;
  } catch (error) {
    dispatch(fetchAllPendingConnectionsFailure(error));
    throw error;
  }
};

const fetchPendingConnectionsCountRequest = () =>
  ({
    type: FETCH_PENDING_CONNECTIONS_COUNT_REQUEST
  } as const);

const fetchPendingConnectionsCountSuccess = (payload: number) =>
  ({
    type: FETCH_PENDING_CONNECTIONS_COUNT_SUCCESS,
    payload
  } as const);

const fetchPendingConnectionsCountFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_PENDING_CONNECTIONS_COUNT_FAILURE,
    error
  } as const;
};

// Count-only sibling of fetchAllPendingConnections for the To Do widget (CPRO-15342). The widget renders
// just the number, so ask for zero records and read @odata.count instead of paginating every pending
// connection. Keep using fetchAllPendingConnections when the records themselves are needed (payment consent).
export const fetchPendingConnectionsCount = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchPendingConnectionsCountRequest());

    const params = new QueryBuilder()
      .top(0)
      .filter(f => f.filterBy('requestType', OdataComparator.Equals, PendingConnectionRequestType.Incoming))
      .toQueryParam();
    const { data } = await axios.get<OdataResponse<IPendingConnection[]>>(
      `/api/v3.1/organizations/${organizationId}/pendingConnections`,
      { params }
    );

    dispatch(fetchPendingConnectionsCountSuccess(data['@odata.count']));
  } catch (error) {
    dispatch(fetchPendingConnectionsCountFailure(error));
  }
};

const fetchActiveConnectionsRequest = () =>
  ({
    type: FETCH_ACTIVE_CONNECTIONS_REQUEST
  } as const);

const fetchActiveConnectionsSuccess = (payload: number) =>
  ({
    type: FETCH_ACTIVE_CONNECTIONS_SUCCESS,
    payload
  } as const);

const fetchActiveConnectionsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_ACTIVE_CONNECTIONS_FAILURE,
    error
  } as const;
};

export const fetchActiveConnectionsCount = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchActiveConnectionsRequest());

    const { data } = await axios.get(`/api/v3.1/organizations/${organizationId}/activeConnections`);

    dispatch(fetchActiveConnectionsSuccess(data['@odata.count']));
  } catch (error) {
    dispatch(fetchActiveConnectionsFailure(error));
    throw error;
  }
};

const fetchConnectionsWithNoServicesCountRequest = () =>
  ({
    type: FETCH_CONNECTIONS_WITH_NO_SERVICES_COUNT_REQUEST
  } as const);

const fetchConnectionsWithNoServicesCountSuccess = (payload: number) =>
  ({
    type: FETCH_CONNECTIONS_WITH_NO_SERVICES_COUNT_SUCCESS,
    payload
  } as const);

const fetchConnectionsWithNoServicesCountFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_CONNECTIONS_WITH_NO_SERVICES_COUNT_FAILURE,
    error
  } as const;
};

// Fetches ALL active connections from the v3.02 endpoint (which includes selectedServices) and
// counts how many have no services selected. Used exclusively by the To Do widget.
export const fetchConnectionsWithNoServicesCount = (
  organizationId: string
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchConnectionsWithNoServicesCountRequest());

    const take = 100;
    let skip = 0;
    let total = 0;
    let count = 0;

    do {
      const { data } = await axios.get<ActiveConnectionsResponseV302>(
        `/api/v3.02/organizations/${organizationId}/activeConnections`,
        { params: { orderBy: '', skip, take } }
      );
      total = data.total;
      count += data.values.filter((c: IConnection) => !c.selectedServices || c.selectedServices.length === 0).length;
      skip += take;
    } while (skip < total);

    dispatch(fetchConnectionsWithNoServicesCountSuccess(count));
  } catch (error) {
    dispatch(fetchConnectionsWithNoServicesCountFailure(error));
  }
};

const applyIncomingConnectionsRequest = () =>
  ({
    type: APPLY_INCOMING_CONNECTIONS_REQUEST
  } as const);

const applyIncomingConnectionsSuccess = () =>
  ({
    type: APPLY_INCOMING_CONNECTIONS_SUCCESS
  } as const);

const createApproveIncomingConnectionsRequest = (organizationId: string, incomingConnection: string) =>
  axios.post<ApproveIncomingConnectionStatus>(`/api/v1/billing/connection/connect`, {
    organizationId,
    connectionOrganizationId: incomingConnection
  });

const processConnectionsSync = async (organizationId: string, incomingConnections: string[]) => {
  const results = [];
  for (let incomingConnection of incomingConnections) {
    let response;
    try {
      response = await createApproveIncomingConnectionsRequest(organizationId, incomingConnection);
    } catch (error) {
      response = error;
    }
    results.push(response);
  }

  return results;
};

export const applyIncomingConnections = ({
  organizationId,
  incomingConnectionsIds,
  afterSuccess,
  afterError
}: ApproveIncomingConnectionsParams): ThunkAction<
  void,
  RootState,
  null,
  Actions | BillingErrorsActions
> => async dispatch => {
  dispatch(applyIncomingConnectionsRequest());

  const result = await processConnectionsSync(organizationId, incomingConnectionsIds);
  const rejected = result.reduce((accum, current) => {
    if (current.status !== 200 || !((current.data as ApproveIncomingConnectionStatus)?.authorized ?? false)) {
      accum.push(
        current.data || {
          authorized: false,
          error: {
            code: TransactionErrorType.Internal,
            description: 'Internal error'
          }
        }
      );
    }

    return accum;
  }, []);

  if (rejected.length) {
    afterError && afterError();
    const hasInsufficientError = rejected.some(
      (item: ApproveIncomingConnectionStatus) =>
        item.error.code === TransactionErrorType.CardDeclined ||
        item.error.code === TransactionErrorType.InsufficientFunds
    );

    if (hasInsufficientError) {
      dispatch(
        insufficientFundsErrorCatch({
          place: 'incomingConnections/authorizeIncomingConnections',
          data: TransactionErrorType.InsufficientFunds
        })
      );
    } else {
      dispatch(
        errorCatch({
          place: 'incomingConnections/authorizeIncomingConnections',
          data: BillingErrorType.IncomingConnections
        })
      );
    }
  } else {
    dispatch(applyIncomingConnectionsSuccess());

    afterSuccess && afterSuccess();
  }
};

export type Actions =
  | ReturnType<typeof applyIncomingConnectionsRequest>
  | ReturnType<typeof applyIncomingConnectionsSuccess>
  | ReturnType<typeof fetchAllPendingConnectionsRequest>
  | ReturnType<typeof fetchAllPendingConnectionsSuccess>
  | ReturnType<typeof fetchAllPendingConnectionsFailure>
  | ReturnType<typeof fetchPendingConnectionsCountRequest>
  | ReturnType<typeof fetchPendingConnectionsCountSuccess>
  | ReturnType<typeof fetchPendingConnectionsCountFailure>
  | ReturnType<typeof fetchActiveConnectionsRequest>
  | ReturnType<typeof fetchActiveConnectionsSuccess>
  | ReturnType<typeof fetchActiveConnectionsFailure>
  | ReturnType<typeof fetchConnectionsWithNoServicesCountRequest>
  | ReturnType<typeof fetchConnectionsWithNoServicesCountSuccess>
  | ReturnType<typeof fetchConnectionsWithNoServicesCountFailure>;
